<template>
  <div
    v-tooltip="tooltipText"
    ref="wrap"
    :class="styles"
  >
    <div
      ref="inner"
      class="club-calendar-points__inner"
    >
      <div class="club-calendar-points__title txt-overflow">
        {{ title }}
      </div>
      <div class="club-calendar-points__points txt-overflow">
        {{ points | price }}
      </div>
      <vue-client-only>
        <div class="club-calendar-points__date txt-overflow">
          {{ promoPeriod }}
        </div>
      </vue-client-only>
    </div>
  </div>
</template>

<script>
import VueClientOnly from 'vue-client-only';

import { ddmmyyyy, ddmm } from '@/utils/date-to-string';

export default {
  components: {
    VueClientOnly
  },

  inject: ['adaptive'],

  props: {
    type: {
      validator(value) {
        return ['cashback', 'employee', 'action'].indexOf(value) !== -1
      },
      default: 'cashback',
    },
    title: {
      type: String,
      default: ''
    },
    points: {
      type: [Number, String],
      required: true,
    },
    start: {
      type: String,
      default: ''
    },
    end: {
      type: String,
      default: ''
    },
    startsEarlier: {
      type: Boolean,
      default: false,
    },
    endsSooner: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    styles() {
      const base = 'club-calendar-points';

      return [base, `${base}_${this.type}`, {
        'square-start': this.startsEarlier,
        'square-end': this.endsSooner,
      }];
    },
    isEmployee() {
      return this.type === 'employee';
    },
    isCashback() {
      return this.type === 'cashback';
    },
    isPromo() {
      return this.type === 'action';
    },
    promoPeriod() {
      if (this.isCashback && this.adaptive.isMobile) {
        return;
      }

      if (this.isCashback || this.isEmployee) {
        return `до ${ddmmyyyy(new Date(this.end))}`;
      }

      return `${ddmm(new Date(this.start))} - ${ddmm(new Date(this.end))}`;
    },
    tooltipText() {
      const bonusVal = this.$options.filters.price(this.points);
      const end = ddmm(new Date(this.end));

      if(!this.isPromo) {
        return `${this.title} ${bonusVal} до ${ddmmyyyy(new Date(this.end))}`;
      }

      const start = ddmm(new Date(this.start));
      const bonusText = this.$options.filters.decl(this.points, 'бонус', ['', 'а', 'ов']);

      return `${start} - ${end} ${this.title} ${bonusVal} ${bonusText}`;
    },
  },

  mounted() {
    this.setPointsPosition();
    window.addEventListener('resize', this.setPointsPosition);
  },

  beforeDestroy() {
    window.removeEventListener('resize', this.setPointsPosition);
  },

  methods: {
    // устанавливает положение бонусов относительно дат
    setPointsPosition() {
      const viewWidth = document.querySelector('.club-points-calendar__widget-days-wrap').offsetWidth;
      const days = [...document.querySelectorAll('.club-points-calendar__widget-day')];

      if (!days.length) return;

      let width = '';
      let offsetLeft;
      let startDate;
      let startDayNode;
      let endDate;
      let endDayNode;

      // если есть стартовая дата то находим соотвествующую ноду и двигаем бонус до ноды даты
      if (this.start) {
        startDate = new Date(this.start);
        startDayNode = days.find(dayNode => {
          const { month, day, year } = dayNode.dataset;

          return month == startDate.getMonth()
            && day == startDate.getDate()
            && year == startDate.getFullYear();
        });

        if(startDayNode) {
          offsetLeft = startDayNode.offsetLeft;
        }
      }

      // если есть конечная находим ноду даты и задаем ширину бонусов до даты
      if (this.end) {
        endDate = new Date(this.end);
        endDayNode = days.find(dayNode => {
          const { month, day, year } = dayNode.dataset;

          return month == endDate.getMonth()
            && day == endDate.getDate()
            && year == endDate.getFullYear();
        });

        // если есть и начало и конец вычисляем разницу между и задаем ширину бонусу
        if (startDayNode && endDayNode ) {
          width = `${(endDayNode.offsetLeft - startDayNode.offsetLeft) + endDayNode.offsetWidth}px`;
        } else if (!startDayNode && endDayNode) {
          // если нет начала но есть конец задаем ширину до даты + еще ширина даты(включительно)
          width = `${endDayNode.offsetLeft + endDayNode.offsetWidth}px`;
        }
      }

      // настраиваем позицию внутреннего блока с контентом
      this.$nextTick(() => {
        const parent = this.$el;
        const pointsContent = parent.querySelector(`.club-calendar-points__inner`);

        if(!pointsContent) return;

        const parentWidth = parent.offsetWidth;

        if (parentWidth > viewWidth) {
          pointsContent.style.width = `${viewWidth}px`;
        } else {
          pointsContent.style.width = `${parentWidth}px`;
        }
      })

      // если нет начала и конца будет на всю ширину графика ибо div
      // если нет конца просто сдвинется marginом и займет всю ширину
      this.$el.style.width = width ? width : '';
      this.$el.style.marginLeft = offsetLeft ? `${offsetLeft}px` : '';
    },
  }
}
</script>

<style lang="scss">
  @import '@kit/txt-overflow.scss';

  .club-calendar-points {
    padding: 4px;
    border-radius: 4px;
    text-align: center;
    min-height: 55px;
    cursor: pointer;

    @include breakpoint("tablet") {
      box-shadow: 0px 2px 8px rgba(0, 0, 0, 0.04),
                  0px 8px 24px rgba(0, 0, 0, 0.03),
                  0px 16px 32px rgba(0, 0, 0, 0.02);
    }

    &.square-start {
      border-top-left-radius: 0 0;
      border-bottom-left-radius: 0 0;
    }

    &.square-end {
      border-top-right-radius: 0 0;
      border-bottom-right-radius: 0 0;
    }

    &_cashback {
      background: $blue_l;
    }

    &_employee {
      background: $coral_f;
    }

    &_action {
      background: $turquoise_f;
    }

    &__inner {
      display: inline-block;
      position: sticky;
      right: 0;
      left: 0;
      // компенсация padding у родителя
      max-width: calc(100% + 8px);
      margin-right: -4px;
      margin-left: -4px;
    }

    &__title {
      font-size: 12px;
    }

    &__points {
      font-size: 18px;
      font-weight: 600;
      color: $white;

      @include breakpoint("tablet") {
        font-size: 20px;
      }
    }

    &__date {
      font-size: 10px;
    }
  }
</style>